"use client";

import Cards from "@/components/content/cards";
import FilterIcon from "@/helpers/svg/filterIcon.svg";
import SearchIcon from "@/helpers/svg/searchIcon.svg";
import { ROUTES } from "@/routes/appRoutes";
import { Clear } from "@mui/icons-material";
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  Spinner,
  useDisclosure,
} from "@nextui-org/react";
import _ from "lodash";
import React, { useState, useEffect, useContext, useRef } from "react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import "react-perfect-scrollbar/dist/css/styles.css";
import {
  addSearchHistoryItem,
  removeSearchHistoryItem,
} from "@/redux/slices/search.slice";
import CategoryFilter, {
  AgeRangeFilter,
  ContentTypeFilter,
  CountryFilter,
  GenreFilter,
  IMDBFilter,
  LanguageFilter,
  YearFilter,
} from "./filters";
import InfiniteScroll from "@/components/common/infinitePagination";
import { useMediaQuery } from "@mui/material";

import axiosInstance from "@/config/axiosInstance";
import { ALL_AGES_CONTENT_AGE_RANGE } from "@/helpers/ageRange.utils";
import { getCMSApiRoute } from "@/helpers/api.util";
import { FiltersContext } from "./filtersContext";
import { trackGtagEvent } from "@/services/gtagAnalyticsService";

const SearchScreenContent = ({ searchedParams }) => {
  const dispatch = useDispatch();
  const { searchHistory } = useSelector((state) => state.Search);
  const [searchData, setSearchData] = useState([]);
  const [inputValue, setInputValue] = useState(searchedParams || "");
  const [debouncedValue, setDebouncedValue] = useState(inputValue);
  const [loading, setLoading] = useState(false);
  const [noResult, setNoResult] = useState(false);
  const [minLenErr, setMinLenErr] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { filters } = useContext(FiltersContext);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const trackedSearchesRef = useRef(new Set());
  const latestRequestRef = useRef(0);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    trackGtagEvent("search", {
      source: "search_screen",
      has_initial_query: Boolean(searchedParams),
    });
  }, [searchedParams]);

  useEffect(() => {
    window.history.replaceState(
      null,
      "",
      ROUTES.search + `/${encodeURIComponent(inputValue)}`
    );

    const handler = _.debounce(() => {
      setDebouncedValue(inputValue);
    }, 500);

    handler();

    return () => {
      handler.cancel();
    };
  }, [inputValue]);

  useEffect(() => {
    if (debouncedValue?.length === 1) {
      latestRequestRef.current += 1;
      setMinLenErr(true);
      setSearchData([]);
      setNoResult(false);
      setHasMore(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const requestId = ++latestRequestRef.current;

    setMinLenErr(false);
    setPageNumber(1);
    fetchData(1, {
      query: debouncedValue,
      filterSnapshot: filters,
      requestId,
      signal: controller.signal,
    });

    return () => controller.abort();
  }, [debouncedValue, filters]);

  const fetchData = async (
    page,
    { query, filterSnapshot, requestId, signal }
  ) => {
    if (page === 1) setLoading(true);
    try {
      const {
        category,
        genre,
        country,
        imdb,
        year,
        kind,
        language,
        contentAgeRange,
      } = filterSnapshot;

      const _skip = undefined;

      const searchTerm = query?.length > 1 ? query : "";
      const _params = _.pickBy(
        {
          title: searchTerm || _skip,
          category: category || _skip,
          genre: genre || _skip,
          country: country || _skip,
          yearStartAt: year?.yearStartAt ? year?.yearStartAt : _skip,
          yearEndAt: year?.yearEndAt ? year?.yearEndAt : _skip,
          imdbRateStartAt: imdb?.imdbRateStartAt
            ? imdb?.imdbRateStartAt
            : _skip,
          imdbRateEndAt: imdb?.imdbRateEndAt ? imdb?.imdbRateEndAt : _skip,
          kind: kind !== "live" && kind !== "online-cinema" && kind,
          audioLang: language || _skip,
          contentAgeRange: contentAgeRange || _skip,
          isTicketing: kind === "online-cinema",
          isLive: kind === "live",
          pageIndex: 40,
          pageNumber: page,
        },
        _.identity
      );

      const response = await axiosInstance.get(getCMSApiRoute("content"), {
        params: _params,
        signal,
      });

      const { data } = response.data;
      const primaryResults = data?.results || [];
      let allAgesResults = [];

      if (
        contentAgeRange &&
        Number(contentAgeRange) !== ALL_AGES_CONTENT_AGE_RANGE
      ) {
        const allAgesResponse = await axiosInstance.get(
          getCMSApiRoute("content"),
          {
            params: {
              ..._params,
              contentAgeRange: ALL_AGES_CONTENT_AGE_RANGE,
            },
            signal,
          }
        );

        allAgesResults = allAgesResponse.data?.data?.results || [];
      }

      const results = _.uniqBy(
        [...primaryResults, ...allAgesResults],
        (item) => item._id || item.slug || item.contentKey
      );

      if (requestId !== latestRequestRef.current) return;

      if (page === 1) {
        setSearchData(results);
        setNoResult(results.length < 1);
        if (searchTerm) {
          const filterValues = {
            category,
            genre,
            country,
            imdb,
            year,
            kind,
            language,
            contentAgeRange,
          };
          const filtersCount = Object.values(filterValues).filter((value) => {
            if (!value) return false;
            if (typeof value === "object")
              return Object.values(value).some(Boolean);
            return true;
          }).length;
          const trackingKey = JSON.stringify({ searchTerm, filterValues });

          if (!trackedSearchesRef.current.has(trackingKey)) {
            trackedSearchesRef.current.add(trackingKey);
            trackGtagEvent("search_performed", {
              search_term: searchTerm,
              result_count: results.length,
              has_results: results.length > 0,
              filters_count: filtersCount,
            });
          }
        }
      } else {
        setSearchData((prev) => [...prev, ...results]);
      }
      setHasMore(primaryResults.length === 40 || allAgesResults.length === 40);
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") {
        console.error("Failed to fetch search results", error);
      }
    } finally {
      if (page === 1 && requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const onLoadMore = async () => {
    if (!loading && !loadingMoreRef.current && hasMore) {
      loadingMoreRef.current = true;
      const nextPage = pageNumber + 1;
      setPageNumber(nextPage);
      const requestId = ++latestRequestRef.current;

      try {
        await fetchData(nextPage, {
          query: debouncedValue,
          filterSnapshot: filters,
          requestId,
        });
      } finally {
        loadingMoreRef.current = false;
      }
    }
  };

  const SearchHistoryRender = () => {
    return (
      hasHydrated &&
      searchHistory?.length > 0 && (
        <div className="w-full mb-1 p-1 md:p-3 ">
          <div className="flex gap-2 w-full items-center justify-center lg:justify-start lg:mb-3">
            <img
              src="/search-history.svg"
              alt="Search history"
              width={16}
              height={16}
            />
            <p className="text-white mb-0 font-light">تاریخچه</p>
          </div>
          <div className="flex gap-3 flex-wrap w-full mt-3 md:mt-0 md:pb-5 md:px-3">
            {searchHistory?.slice(0, 7)?.map((item) => (
              <Chip
                key={"searchHistory_" + item}
                className="dark text-white cursor-pointer"
                onClose={() => {
                  dispatch(removeSearchHistoryItem(item));
                }}
                variant="faded"
                onClick={() => setInputValue(item)}
              >
                {item}
              </Chip>
            ))}
          </div>
        </div>
      )
    );
  };

  const SearchNoResult = () => (
    <div className="flex justify-center gap-2 items-center py-5">
      <img src="/search-no-result.svg" alt="No Result" width={16} height={16} />
      <span className="text-primary-white font-bold">محتوایی یافت نشد</span>
    </div>
  );

  return (
    <div className="h-auto min-h-[100vh] pt-24 md:container md:mx-auto mx-3">
      <Link
        href={ROUTES.comingSoon}
        className="mb-5 inline-flex min-h-11 items-center rounded-full border border-white/25 bg-white/10 px-5 font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
      >
        محتوای بزودی
      </Link>
      <div className="flex flex-col justify-between  min-h-[170px] h-auto bg-[#0F0B0B] rounded-[20px] p-5 border-1 border-[#3d3c3c] overflow-x-hidden">
        <div className="hidden md:block overflow-y-auto">
          {!isMobile && (
            <div className="flex gap-2 items-center w-fit min-w-full overflow-y-hidden pb-4">
              <ContentTypeFilter />
              <LanguageFilter />
              <CountryFilter />
              <AgeRangeFilter />
              <CategoryFilter />
              <GenreFilter />
              <YearFilter />
              <IMDBFilter />
            </div>
          )}
        </div>
        <div className="md:hidden w-full">
          {isMobile && (
            <>
              <Button
                className="bg-[#262626] rounded-full text-white"
                fullWidth
                onClick={onOpen}
              >
                <FilterIcon />
                فیلترها
              </Button>
              <Modal
                className="dark"
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                scrollBehavior="outside"
              >
                <ModalContent>
                  {(onClose) => (
                    <ModalBody className="pb-5">
                      <div className="flex flex-col justify-between md:flex-wrap overflow-x-auto items-center pb-3 pt-10 gap-3">
                        <ContentTypeFilter />
                        <LanguageFilter />
                        <CountryFilter />
                        <AgeRangeFilter />
                        <CategoryFilter />
                        <GenreFilter />
                        <YearFilter />
                        <IMDBFilter />
                      </div>
                      <Button
                        variant="solid"
                        className="bg-[#6A0000] text-white"
                        color="danger"
                        fullWidth
                        size="lg"
                        onPress={onClose}
                      >
                        نمایش
                      </Button>
                    </ModalBody>
                  )}
                </ModalContent>
              </Modal>
            </>
          )}
        </div>
        <Input
          className="input-button ml-4 mt-5 w-full"
          classNames={{
            inputWrapper: "text-primary-white rounded-full",
            input: "!text-primary-white pb-1 pr-3",
            label: "mr-3",
          }}
          variant="solid"
          value={inputValue}
          fullWidth
          onChange={({ target: { value } }) => setInputValue(value)}
          label="جستجو..."
          isClearable={false}
          size={isMobile ? "md" : "lg"}
          endContent={
            <div className="flex justify-center gap-3 items-center ml-2 pb-2 cursor-pointer">
              {inputValue?.length > 0 && (
                <Clear
                  sx={{ color: "dimgray" }}
                  onClick={() => setInputValue("")}
                />
              )}
              <SearchIcon />
            </div>
          }
        />
        <SearchHistoryRender />
      </div>
      <div className="flex justify-center items-center lg:my-12">
        {minLenErr && (
          <p className="text-center text-white my-5">
            عبارت جستجو باید حداقل دو حرفی باشد
          </p>
        )}
        {debouncedValue?.length > 1 && loading ? (
          <div className="flex justify-center items-center h-[100px] p-3 ">
            <Spinner color="danger" />
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center h-fit py-3 md:py-5 w-full">
            {!noResult ? (
              <InfiniteScroll hasMore={hasMore} loadMore={onLoadMore}>
                <Cards
                  items={searchData || []}
                  type="movie"
                  isSwiper={false}
                  noTag={false}
                  noMX={true}
                  effectAction={(value) =>
                    dispatch(addSearchHistoryItem(value))
                  }
                />
              </InfiniteScroll>
            ) : (
              <SearchNoResult />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchScreenContent;
